import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Clock, MessageSquare, Send, Lock, Unlock,
  CheckCircle, ArrowRight, Paperclip, FileText, Download,
  Trash2, Upload, Tag, FolderOpen, Layers, User, Building,
  Phone, Mail, Calendar, AlertTriangle, Edit3, Eye, History,
  MessageCircle, Check, CheckCheck, Scale
} from 'lucide-react';
import toast from 'react-hot-toast';
import { atendimentosAPI, setoresAPI, authAPI, documentosAPI, beneficiariosAPI, whatsappAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ParecerModal from '../components/ParecerModal';
import MencaoTextarea from '../components/MencaoTextarea';
import { malhaAPI } from '../services/api';
import AlertasBeneficiario from '../components/AlertasBeneficiario';

const Atendimento = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, temNivel } = useAuth();

  const [atendimento, setAtendimento] = useState(null);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('anotacoes');

  const [novaAnotacao, setNovaAnotacao] = useState('');
  const [mencionados, setMencionados] = useState([]);
  const [showParecer, setShowParecer] = useState(false);
  const [subtarefas, setSubtarefas] = useState([]);
  const [anotacaoInterna, setAnotacaoInterna] = useState(true);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [showCadeadoModal, setShowCadeadoModal] = useState(false);
  const [showContatoModal, setShowContatoModal] = useState(false);
  const [transferData, setTransferData] = useState({ setor_destino_id: '', motivo: '' });
  const [finalizarMotivo, setFinalizarMotivo] = useState('');
  const [finalizarResultado, setFinalizarResultado] = useState('');   // só tarefa: opção do tipo

  const [usuarios, setUsuarios] = useState([]);
  const [cadeadoAtivo, setCadeadoAtivo] = useState(false);
  const [usuariosAutorizados, setUsuariosAutorizados] = useState([]);

  const [documentos, setDocumentos] = useState([]);
  const [tiposDocumento, setTiposDocumento] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadTipoId, setUploadTipoId] = useState('');
  const [uploadDescricao, setUploadDescricao] = useState('');
  const [uploading, setUploading] = useState(false);

  // WhatsApp
  const [whatsappData, setWhatsappData] = useState({ conversas: [], mensagens: [] });
  const [whatsappMsg, setWhatsappMsg] = useState('');
  const [enviandoWhatsapp, setEnviandoWhatsapp] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);

  // Edição de contato
  const [contatoForm, setContatoForm] = useState({ telefone: '', email: '' });
  const [salvandoContato, setSalvandoContato] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [atendRes, setoresRes, docsRes, tiposRes] = await Promise.all([
        atendimentosAPI.get(id),
        setoresAPI.list(),
        documentosAPI.listarPorAtendimento(id),
        documentosAPI.listarTipos('atendimento'),
      ]);
      setAtendimento(atendRes.data);
      // Tarefa aberta por régua da Malha: parecer + subtarefas de aprovação
      if (atendRes.data?.natureza === 'tarefa' && String(atendRes.data?.origem || '').startsWith('regua:')) {
        malhaAPI.subtarefas(id).then((r) => setSubtarefas(r.data || [])).catch(() => setSubtarefas([]));
      } else setSubtarefas([]);
      setSetores(setoresRes.data);
      setDocumentos(docsRes.data);
      setTiposDocumento(tiposRes.data);
      // Carregar dados do WhatsApp
      try {
        const wpRes = await whatsappAPI.mensagensPorAtendimento(id);
        setWhatsappData(wpRes.data);
      } catch (e) {
        // WhatsApp pode nao ter conversas - ok
        setWhatsappData({ conversas: [], mensagens: [] });
      }
    } catch (error) {
      toast.error('Erro ao carregar atendimento');
      navigate('/kanban');
    }
    setLoading(false);
  };

  const handleAddAnotacao = async () => {
    if (!novaAnotacao.trim()) {
      toast.error('Descreva a ocorrencia');
      return;
    }
    try {
      await atendimentosAPI.addAnotacao(id, {
        conteudo: novaAnotacao,
        interna: anotacaoInterna,
        mencionados,
      });
      toast.success('Registro adicionado');
      setNovaAnotacao('');
      setMencionados([]);
      loadData();
    } catch (error) {
      toast.error('Erro ao adicionar registro');
    }
  };

  const handleTransferir = async () => {
    if (!transferData.setor_destino_id || !transferData.motivo) {
      toast.error('Preencha todos os campos');
      return;
    }
    try {
      const response = await atendimentosAPI.transferir(id, transferData);
      const novoProtocolo = response.data?.protocolo || '—';
      toast.success(
        `Atendimento transferido. Novo protocolo ${novoProtocolo} foi aberto no setor destino.`,
        { duration: 5000 }
      );
      setShowTransferModal(false);
      // NÃO navegamos para o atendimento transferido — ele agora pertence ao
      // outro setor. Voltamos pro Kanban para evitar que o operador finalize
      // por engano um card que não é mais dele.
      navigate('/kanban');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao transferir');
    }
  };

  const handleFinalizar = async () => {
    if (!finalizarMotivo) {
      toast.error('Informe o motivo da finalizacao');
      return;
    }
    const opcoes = atendimento?.natureza === 'tarefa' ? (atendimento.tipo_tarefa?.resultados || []) : [];
    if (opcoes.length && !finalizarResultado) {
      toast.error('Informe o resultado da tarefa');
      return;
    }
    try {
      await atendimentosAPI.finalizar(id, finalizarMotivo, opcoes.length ? finalizarResultado : undefined);
      toast.success(atendimento?.natureza === 'tarefa' ? 'Tarefa finalizada' : 'Atendimento finalizado com sucesso');
      setShowFinalizarModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao finalizar');
    }
  };

  const handleOpenCadeadoModal = async () => {
    try {
      const usuariosRes = await authAPI.listAllUsers();
      setUsuarios(usuariosRes.data);
      setCadeadoAtivo(atendimento.cadeado || false);
      const nomesAutorizados = atendimento.usuarios_autorizados || [];
      const idsAutorizados = usuariosRes.data
        .filter((u) => nomesAutorizados.includes(u.nome_completo))
        .map((u) => u.id);
      setUsuariosAutorizados(idsAutorizados);
      setShowCadeadoModal(true);
    } catch (error) {
      toast.error('Erro ao carregar usuarios');
    }
  };

  const handleSalvarCadeado = async () => {
    try {
      await atendimentosAPI.gerenciarCadeado(id, cadeadoAtivo, cadeadoAtivo ? usuariosAutorizados : null);
      toast.success('Configuracoes de sigilo atualizadas!');
      setShowCadeadoModal(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar sigilo');
    }
  };

  const handleToggleUsuario = (usuarioId) => {
    if (usuariosAutorizados.includes(usuarioId)) {
      setUsuariosAutorizados(usuariosAutorizados.filter((uid) => uid !== usuarioId));
    } else {
      setUsuariosAutorizados([...usuariosAutorizados, usuarioId]);
    }
  };

  const handleOpenContatoModal = () => {
    setContatoForm({
      telefone: atendimento.beneficiario.telefone || '',
      email: atendimento.beneficiario.email || '',
    });
    setShowContatoModal(true);
  };

  const handleSalvarContato = async () => {
    setSalvandoContato(true);
    try {
      await beneficiariosAPI.atualizar(atendimento.beneficiario.id, {
        telefone_principal: contatoForm.telefone || null,
        email: contatoForm.email || null,
      });
      toast.success('Contato atualizado!');
      setShowContatoModal(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar contato');
    }
    setSalvandoContato(false);
  };

  const handleOpenUploadModal = () => {
    setUploadFiles([]);
    setUploadTipoId('');
    setUploadDescricao('');
    setShowUploadModal(true);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles(files);
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error('Selecione pelo menos um arquivo');
      return;
    }
    setUploading(true);
    try {
      for (const file of uploadFiles) {
        await documentosAPI.upload(id, file, uploadTipoId || null, uploadDescricao || null);
      }
      toast.success(uploadFiles.length === 1 ? 'Documento enviado' : `${uploadFiles.length} documentos enviados`);
      setShowUploadModal(false);
      const docsRes = await documentosAPI.listarPorAtendimento(id);
      setDocumentos(docsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar documento');
    }
    setUploading(false);
  };

  const handleDeleteDocumento = async (docId) => {
    if (!window.confirm('Tem certeza que deseja excluir este documento?')) return;
    try {
      await documentosAPI.deletar(id, docId);
      toast.success('Documento removido');
      setDocumentos(documentos.filter((d) => d.id !== docId));
    } catch (error) {
      toast.error('Erro ao remover documento');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentosAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.nome_original);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar documento');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDateTime = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('pt-BR');
  };

  const isAtendimentoFinalizado = atendimento?.status === 'finalizado';
  const ehParecer = atendimento?.natureza === 'tarefa' && String(atendimento?.origem || '').startsWith('regua:');
  const parecerPendente = subtarefas.find((s) => !s.concluida_em);
  const podeDecidir = (s) => s && (s.responsavel_id === user?.id || temNivel('radar_carteira', 'gestor'));
  const tempoEspera = (seg) => (seg == null ? '—' : seg < 3600 ? `${Math.max(1, Math.round(seg / 60))} min` : seg < 86400 ? `${Math.round(seg / 3600)} h` : `${Math.round(seg / 86400)} dia(s)`);
  const decidir = async (s, aprovar) => {
    let comentario = null;
    if (!aprovar) {
      comentario = window.prompt('O que falta para aprovar? (volta para quem registrou o parecer)');
      if (!comentario) return;
    }
    try {
      await malhaAPI.decidir(s.id, aprovar, comentario);
      toast.success(aprovar ? 'Justificativa aprovada: padrão esperado em vigor e tarefa finalizada' : 'Justificativa devolvida');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao decidir'); }
  };
  const ehTarefa = atendimento?.natureza === 'tarefa';
  const semContato = atendimento && atendimento.beneficiario && !atendimento.beneficiario.telefone && !atendimento.beneficiario.email;

  const getStatusBadge = (status) => {
    const styles = {
      aberto: { bg: '#e3f2fd', color: '#1976d2' },
      em_andamento: { bg: '#fff3e0', color: '#f57c00' },
      aguardando: { bg: '#fce4ec', color: '#c2185b' },
      finalizado: { bg: '#e8f5e9', color: '#388e3c' },
      transferido: { bg: '#f3e5f5', color: '#7b1fa2' },
    };
    const s = styles[status] || styles.aberto;
    return (
      <span style={{
        background: s.bg, color: s.color,
        padding: '0.25rem 0.75rem', borderRadius: '20px',
        fontSize: '0.85rem', fontWeight: 500, textTransform: 'capitalize'
      }}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPrioridadeBadge = (prioridade) => {
    const styles = {
      normal: { bg: '#e8f5e9', color: '#388e3c' },
      alta: { bg: '#fff3e0', color: '#f57c00' },
      urgente: { bg: '#ffebee', color: '#d32f2f' },
    };
    const s = styles[prioridade] || styles.normal;
    return (
      <span style={{
        background: s.bg, color: s.color,
        padding: '0.25rem 0.75rem', borderRadius: '20px',
        fontSize: '0.85rem', fontWeight: 500, textTransform: 'capitalize'
      }}>
        {prioridade}
      </span>
    );
  };

  const tabs = [
    { id: 'anotacoes', label: 'Registro de Atendimento', icon: MessageSquare, count: atendimento?.anotacoes?.length },
    { id: 'geral', label: 'Visao Geral', icon: Eye },
    { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, count: whatsappData.mensagens?.length },
    { id: 'documentos', label: 'Documentos', icon: Paperclip, count: documentos.length },
    { id: 'historico', label: 'Historico', icon: History, count: atendimento?.movimentacoes?.length },
  ];

  const carregarWhatsapp = async () => {
    setWhatsappLoading(true);
    try {
      const wpRes = await whatsappAPI.mensagensPorAtendimento(id);
      setWhatsappData(wpRes.data);
    } catch (e) {
      setWhatsappData({ conversas: [], mensagens: [] });
    }
    setWhatsappLoading(false);
  };

  const handleEnviarWhatsapp = async (conversaId) => {
    if (!whatsappMsg.trim() || enviandoWhatsapp) return;
    setEnviandoWhatsapp(true);
    try {
      await whatsappAPI.enviarMensagem(conversaId, whatsappMsg.trim());
      setWhatsappMsg('');
      await carregarWhatsapp();
      await loadData();
    } catch (error) {
      toast.error('Erro ao enviar mensagem WhatsApp');
    }
    setEnviandoWhatsapp(false);
  };

  const handleEnviarOuIniciarWhatsapp = async () => {
    if (!whatsappMsg.trim() || enviandoWhatsapp) return;
    setEnviandoWhatsapp(true);
    try {
      // Se ja tem conversa aberta, envia nela
      const conversaAberta = whatsappData.conversas.find(c => c.status === 'aberta');
      if (conversaAberta) {
        await whatsappAPI.enviarMensagem(conversaAberta.id, whatsappMsg.trim());
      } else {
        // Criar nova conversa e enviar primeira mensagem
        await whatsappAPI.iniciarConversa({
          beneficiario_id: atendimento.beneficiario_id,
          atendimento_id: atendimento.id,
          telefone: atendimento.beneficiario?.telefone,
          mensagem: whatsappMsg.trim(),
        });
      }
      setWhatsappMsg('');
      await carregarWhatsapp();
      await loadData();
      toast.success('Mensagem enviada via WhatsApp');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar mensagem WhatsApp');
    }
    setEnviandoWhatsapp(false);
  };

  const formatarHoraWpp = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const hoje = new Date();
    if (d.toDateString() === hoje.toDateString()) {
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
      ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!atendimento) {
    return <div className="empty-state">Atendimento nao encontrado</div>;
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
      {/* Voltar */}
      <button className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => navigate(-1)}>
        <ArrowLeft size={18} />
        Voltar
      </button>

      {/* Alertas do beneficiario (Sit. 13): ANTES do protocolo — e aqui que a
          decisao errada custa caro. */}
      {atendimento.beneficiario?.cpf && (
        <AlertasBeneficiario cpf={String(atendimento.beneficiario.cpf).replace(/\D/g, '')} />
      )}

      {/* ========== HEADER ========== */}
      <div className="search-box" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 style={{ color: 'var(--primary)', marginBottom: '0.25rem', fontSize: '1.5rem' }}>
              {atendimento.protocolo}
            </h2>
            {atendimento.protocolo_pai_id && (
              <span style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                Derivado do protocolo #{atendimento.protocolo_pai_id}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {!isAtendimentoFinalizado && (
              <>
                <button
                  className={`btn ${atendimento.cadeado ? 'btn-warning' : 'btn-secondary'}`}
                  onClick={handleOpenCadeadoModal}
                  title="Gerenciar Sigilo"
                >
                  {atendimento.cadeado ? <Lock size={16} /> : <Unlock size={16} />}
                  Sigilo
                </button>
                {ehParecer && !parecerPendente && (
                  <button className="btn btn-warning" onClick={() => setShowParecer(true)} title="Concluir a análise das contas deste caso">
                    <Scale size={16} />
                    Registrar parecer
                  </button>
                )}
                <button className="btn btn-primary" onClick={() => setShowTransferModal(true)}>
                  <ArrowRight size={16} />
                  Transferir
                </button>
                {!ehParecer && (
                  <button className="btn btn-success" onClick={() => setShowFinalizarModal(true)}>
                    <CheckCircle size={16} />
                    Finalizar
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {/* Status, Prioridade e Info do Beneficiário */}
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {getStatusBadge(atendimento.status)}
          {getPrioridadeBadge(atendimento.prioridade)}
          {atendimento.cadeado && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#f39c12', fontSize: '0.85rem' }}>
              <Lock size={14} /> Sigilo
            </span>
          )}
          <span style={{ color: 'var(--gray-300)' }}>|</span>
          {ehTarefa && (
            <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em', background: '#eef2f7', color: '#2c3e50', borderRadius: 4, padding: '2px 6px' }}
              title="Tarefa interna: não avisa o beneficiário nem dispara pesquisa ao finalizar">
              TAREFA{atendimento.tipo_tarefa ? ` · ${atendimento.tipo_tarefa.nome}` : ''}
            </span>
          )}
          {atendimento.beneficiario ? (
            <>
              <span style={{ fontSize: '0.85rem', color: 'var(--gray-600)', fontWeight: 500 }}>
                {atendimento.beneficiario.nome_completo}
              </span>
              <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                CPF: {atendimento.beneficiario.cpf}
              </span>
              {atendimento.beneficiario.telefone && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8rem', color: 'var(--gray-500)' }}>
                  <Phone size={12} /> {atendimento.beneficiario.telefone}
                </span>
              )}
              {atendimento.beneficiario.parentesco && atendimento.beneficiario.parentesco !== 'TITULAR' && (
                <>
                  <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                    ({atendimento.beneficiario.parentesco})
                  </span>
                  {atendimento.beneficiario.titular && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>
                      Titular: {atendimento.beneficiario.titular.nome}
                    </span>
                  )}
                </>
              )}
            </>
          ) : (
            <span style={{ fontSize: '0.8rem', color: 'var(--gray-400)' }}>sem beneficiário</span>
          )}
        </div>

        {/* Alerta sem contato */}
        {semContato && (
          <div
            onClick={handleOpenContatoModal}
            style={{
              marginTop: '0.75rem',
              padding: '0.5rem 0.75rem',
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              animation: 'pulse-alert 2s infinite',
            }}
          >
            <AlertTriangle size={16} style={{ color: '#e67e22' }} />
            <span style={{ fontSize: '0.85rem', color: '#856404', fontWeight: 500 }}>
              Beneficiario sem telefone e sem e-mail. Clique para cadastrar
            </span>
          </div>
        )}
      </div>

      {/* ========== TABS ========== */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1rem', borderBottom: '2px solid var(--gray-200)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.6rem 1rem',
              border: 'none', background: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
              color: activeTab === tab.id ? 'var(--primary)' : 'var(--gray-500)',
              fontWeight: activeTab === tab.id ? 600 : 400,
              fontSize: '0.9rem', cursor: 'pointer',
              marginBottom: '-2px',
              transition: 'all 0.2s',
            }}
          >
            <tab.icon size={16} />
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                background: activeTab === tab.id ? 'var(--primary)' : 'var(--gray-300)',
                color: 'white', fontSize: '0.7rem',
                padding: '0.1rem 0.4rem', borderRadius: '10px', minWidth: '18px', textAlign: 'center',
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ========== TAB CONTENT ========== */}

      {/* VISAO GERAL */}
      {activeTab === 'geral' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {/* Classificacao */}
          <div className="search-box">
            <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Layers size={16} /> Classificacao
            </h4>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {atendimento.categoria && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  background: atendimento.categoria.cor || '#3498db', color: 'white',
                  padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem', fontWeight: 500
                }}>
                  <FolderOpen size={14} /> {atendimento.categoria.nome}
                </span>
              )}
              {atendimento.tipo && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  background: '#f0f0f0', color: '#333',
                  padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem'
                }}>
                  <Tag size={14} /> {atendimento.tipo.nome}
                </span>
              )}
              {atendimento.subtipo && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  background: '#fff', border: '1px solid #ddd', color: '#666',
                  padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.85rem'
                }}>
                  {atendimento.subtipo.nome}
                </span>
              )}
            </div>
            {atendimento.campos_extras && Object.keys(atendimento.campos_extras).length > 0 && (
              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--gray-200)' }}>
                {Object.entries(atendimento.campos_extras).map(([key, value]) => {
                  if (!value) return null;
                  const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                    <div key={key} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.35rem', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--gray-500)', minWidth: '120px' }}>{label}:</span>
                      <span style={{ color: 'var(--gray-700)' }}>{value}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Atendimento Info */}
          <div className="search-box">
            <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Building size={16} /> Atendimento
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[
                ['Setor', atendimento.setor.nome],
                ['Coluna', atendimento.coluna.nome],
                ['Atendente', atendimento.atendente?.nome || 'Sem dono'],
                ['Canal', atendimento.canal_entrada],
              ].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--gray-500)' }}>{label}</span>
                  <span style={{ fontWeight: 500, textTransform: 'capitalize' }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Motivo */}
          <div className="search-box" style={{ gridColumn: '1 / -1' }}>
            <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.75rem' }}>
              {ehTarefa ? 'O que precisa ser feito' : 'Motivo do Atendimento'}
            </h4>
            <p style={{ color: 'var(--gray-700)', lineHeight: 1.6, margin: 0 }}>
              {atendimento.motivo}
            </p>
          </div>

          {/* Tarefa: tipo, checklist e resultado (card interno, pode não ter beneficiário) */}
          {ehTarefa && (
            <div className="search-box">
              <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0 0 0.75rem' }}>
                <CheckCircle size={16} /> Tarefa
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.85rem' }}>
                <div><span style={{ color: 'var(--gray-500)' }}>Tipo:</span> <strong>{atendimento.tipo_tarefa?.nome || '—'}</strong></div>
                <div><span style={{ color: 'var(--gray-500)' }}>Origem:</span> {atendimento.origem || 'manual'}{atendimento.chave_origem ? ` · ${atendimento.chave_origem}` : ''}</div>
                {atendimento.campos_extras?.resultado && (
                  <div><span style={{ color: 'var(--gray-500)' }}>Resultado:</span> <strong>{atendimento.campos_extras.resultado}</strong></div>
                )}
                {atendimento.tipo_tarefa?.checklist?.length > 0 && (
                  <div>
                    <div style={{ color: 'var(--gray-500)', marginBottom: 2 }}>Passos:</div>
                    <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>{atendimento.tipo_tarefa.checklist.map((p, i) => <li key={i}>{p}</li>)}</ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Beneficiário detalhes */}
          {atendimento.beneficiario && (
          <div className="search-box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-600)', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <User size={16} /> Beneficiario
              </h4>
              <button
                className="btn btn-secondary"
                style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem' }}
                onClick={handleOpenContatoModal}
                title="Editar contato"
              >
                <Edit3 size={12} /> Editar
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <div style={{ fontWeight: 600 }}>{atendimento.beneficiario.nome_completo}</div>
              <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>CPF: {atendimento.beneficiario.cpf}</div>
              {atendimento.beneficiario.telefone ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                  <Phone size={12} /> {atendimento.beneficiario.telefone}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: '#e67e22' }}>
                  <Phone size={12} /> Sem telefone
                </div>
              )}
              {atendimento.beneficiario.email ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: 'var(--gray-600)' }}>
                  <Mail size={12} /> {atendimento.beneficiario.email}
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.85rem', color: '#e67e22' }}>
                  <Mail size={12} /> Sem e-mail
                </div>
              )}
              {atendimento.beneficiario.parentesco && (
                <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginTop: '0.25rem' }}>
                  Parentesco: <strong>{atendimento.beneficiario.parentesco}</strong>
                  {atendimento.beneficiario.titular && atendimento.beneficiario.parentesco !== 'TITULAR' && (
                    <span> · Titular: {atendimento.beneficiario.titular.nome}</span>
                  )}
                </div>
              )}
            </div>
          </div>
          )}

          {/* Datas */}
          <div className="search-box">
            <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Calendar size={16} /> Datas
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span style={{ color: 'var(--gray-500)' }}>Criado em</span>
                <span style={{ fontWeight: 500 }}>{formatDateTime(atendimento.criado_em)}</span>
              </div>
              {atendimento.finalizado_em && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                  <span style={{ color: 'var(--gray-500)' }}>Finalizado</span>
                  <span style={{ fontWeight: 500 }}>{formatDateTime(atendimento.finalizado_em)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ANOTACOES */}
      {activeTab === 'anotacoes' && subtarefas.length > 0 && (
        <div className="search-box" style={{ marginBottom: '1rem' }}>
          <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-600)', margin: '0 0 0.6rem', display: 'flex', alignItems: 'center', gap: 6 }}><Scale size={16} /> Aprovação do parecer</h4>
          {subtarefas.map((s) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0.5rem 0', borderTop: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                background: !s.concluida_em ? '#fef5e7' : s.resultado === 'aprovado' ? '#e8f6f4' : '#fdecea',
                color: !s.concluida_em ? '#b9770e' : s.resultado === 'aprovado' ? '#1e8449' : '#c0392b' }}>
                {!s.concluida_em ? 'Aguardando' : s.resultado === 'aprovado' ? 'Aprovado' : 'Devolvido'}
              </span>
              <span style={{ fontSize: '0.85rem' }}>
                {s.solicitante} pediu a <b>@{s.responsavel}</b>
                {s.dados && <> · R$ {Number(s.dados.faixa_min).toLocaleString('pt-BR')} a {Number(s.dados.faixa_max).toLocaleString('pt-BR')}/mês até {new Date(s.dados.valido_ate + 'T12:00:00').toLocaleDateString('pt-BR')}</>}
              </span>
              <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)' }} title="Tempo entre o pedido e a decisão">
                <Clock size={12} /> espera: {tempoEspera(s.espera_segundos)}
              </span>
              {s.comentario && <span style={{ fontSize: '0.78rem', color: 'var(--gray-600)' }}>“{s.comentario}”</span>}
              {!s.concluida_em && podeDecidir(s) && (
                <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                  <button className="btn btn-success" style={{ padding: '0.3rem 0.7rem' }} onClick={() => decidir(s, true)}>Aprovar</button>
                  <button className="btn btn-secondary" style={{ padding: '0.3rem 0.7rem' }} onClick={() => decidir(s, false)}>Devolver</button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {activeTab === 'anotacoes' && (
        <div className="search-box">
          {/* Motivo do Atendimento */}
          <div style={{ background: '#f0faf8', border: '1px solid #b2dfdb', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: '0.35rem', margin: 0 }}>{ehTarefa ? 'O que precisa ser feito' : 'Motivo do Atendimento'}</h4>
            <p style={{ color: 'var(--gray-700)', fontSize: '0.9rem', lineHeight: 1.5, margin: 0 }}>{atendimento.motivo}</p>
          </div>

          {atendimento.anotacoes.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', textAlign: 'center', padding: '2rem' }}>
              Nenhum registro ainda
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
              {atendimento.anotacoes.map((anot) => {
                const isWhatsapp = anot.conteudo?.startsWith('[WhatsApp');
                return (
                  <div key={anot.id} style={{
                    background: isWhatsapp ? '#e8f5e9' : anot.interna ? '#fffbeb' : '#f8f9fa',
                    border: `1px solid ${isWhatsapp ? '#a5d6a7' : anot.interna ? '#fcd34d' : '#e5e7eb'}`,
                    borderLeft: isWhatsapp ? '3px solid #25D366' : undefined,
                    borderRadius: '8px', padding: '0.75rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span style={{ fontWeight: 500, fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        {isWhatsapp && <MessageCircle size={14} style={{ color: '#25D366' }} />}
                        {anot.usuario}
                      </span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                        {formatDateTime(anot.criado_em)}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--gray-700)' }}>{anot.conteudo}</p>
                    {isWhatsapp && (
                      <span style={{
                        fontSize: '0.7rem', background: '#c8e6c9', color: '#2e7d32',
                        padding: '0.15rem 0.4rem', borderRadius: '4px', marginTop: '0.35rem', display: 'inline-block'
                      }}>
                        WhatsApp
                      </span>
                    )}
                    {anot.interna && !isWhatsapp && (
                      <span style={{
                        fontSize: '0.7rem', background: '#fef3c7', color: '#92400e',
                        padding: '0.15rem 0.4rem', borderRadius: '4px', marginTop: '0.35rem', display: 'inline-block'
                      }}>
                        Interno
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!isAtendimentoFinalizado && (
            <div style={{ borderTop: '1px solid var(--gray-200)', paddingTop: '1rem' }}>
              <MencaoTextarea
                rows={3}
                placeholder="Descrever ocorrência… use @ para citar alguém (recebe alerta no sino)"
                value={novaAnotacao}
                onChange={setNovaAnotacao}
                onMencionados={setMencionados}
                style={{ marginBottom: '0.5rem' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={anotacaoInterna}
                    onChange={(e) => setAnotacaoInterna(e.target.checked)}
                  />
                  Interna
                </label>
                <button className="btn btn-primary" onClick={handleAddAnotacao} style={{ padding: '0.4rem 0.75rem' }}>
                  <Send size={14} />
                  Enviar
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* WHATSAPP */}
      {activeTab === 'whatsapp' && (
        <div className="search-box">
          {/* Header com info do contato */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: '0.75rem', background: 'var(--brand, #20a490)', borderRadius: '8px',
            color: '#fff', marginBottom: '1rem'
          }}>
            <div style={{
              width: '40px', height: '40px', borderRadius: '50%',
              background: 'rgba(255,255,255,0.2)', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: '1.1rem'
            }}>
              {(atendimento.beneficiario?.nome_completo || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>
                {atendimento.beneficiario?.nome_completo}
              </div>
              <div style={{ fontSize: '0.8rem', opacity: 0.9, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <Phone size={12} />
                {atendimento.beneficiario?.telefone || 'Sem telefone cadastrado'}
              </div>
            </div>
            {whatsappData.conversas.length > 0 && (
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {whatsappData.conversas.map(c => (
                  <span key={c.id} style={{
                    padding: '0.15rem 0.5rem', borderRadius: '10px', fontSize: '0.7rem',
                    background: c.status === 'aberta' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                  }}>
                    {c.status}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Area de mensagens */}
          <div style={{
            background: '#e5ddd5',
            borderRadius: '8px',
            padding: '1rem',
            minHeight: '200px',
            maxHeight: '400px',
            overflowY: 'auto',
            marginBottom: '1rem',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'a\' patternUnits=\'userSpaceOnUse\' width=\'40\' height=\'40\'%3E%3Cpath d=\'M0 20h40M20 0v40\' fill=\'none\' stroke=\'%23dcd6cc\' stroke-width=\'.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'100%25\' height=\'100%25\' fill=\'%23e5ddd5\'/%3E%3Crect width=\'100%25\' height=\'100%25\' fill=\'url(%23a)\'/%3E%3C/svg%3E")',
          }}>
            {whatsappData.mensagens.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#999', padding: '2rem' }}>
                <MessageCircle size={36} style={{ opacity: 0.3, marginBottom: '0.5rem' }} />
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Nenhuma mensagem ainda</p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem' }}>Envie a primeira mensagem para iniciar a conversa</p>
              </div>
            ) : (
              whatsappData.mensagens.map(msg => (
                <div key={msg.id} style={{
                  display: 'flex',
                  justifyContent: msg.direcao === 'saida' ? 'flex-end' : 'flex-start',
                  marginBottom: '4px',
                }}>
                  <div style={{
                    maxWidth: '70%',
                    padding: '6px 10px 4px',
                    borderRadius: msg.direcao === 'saida' ? '8px 0 8px 8px' : '0 8px 8px 8px',
                    background: msg.direcao === 'saida' ? '#dcf8c6' : '#fff',
                    boxShadow: '0 1px 1px rgba(0,0,0,0.1)',
                  }}>
                    {msg.direcao === 'saida' && msg.enviado_por_nome && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--brand, #20a490)', fontWeight: 600, marginBottom: '2px' }}>
                        {msg.enviado_por_nome}
                      </div>
                    )}
                    {msg.direcao === 'entrada' && (
                      <div style={{ fontSize: '0.7rem', color: '#e67e22', fontWeight: 600, marginBottom: '2px' }}>
                        {atendimento.beneficiario?.nome_completo || 'Beneficiario'}
                      </div>
                    )}
                    <div style={{ fontSize: '0.88rem', color: '#111', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {msg.conteudo}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                      <span style={{ fontSize: '0.65rem', color: '#999' }}>
                        {formatarHoraWpp(msg.criado_em)}
                      </span>
                      {msg.direcao === 'saida' && (
                        msg.status_envio === 'read' ? <CheckCheck size={14} style={{ color: '#3498db' }} /> :
                        msg.status_envio === 'delivered' ? <CheckCheck size={14} style={{ color: '#999' }} /> :
                        <Check size={14} style={{ color: '#999' }} />
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Input de envio - sempre visivel se tem telefone */}
          {atendimento.beneficiario?.telefone ? (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                value={whatsappMsg}
                onChange={(e) => setWhatsappMsg(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && whatsappMsg.trim()) {
                    handleEnviarOuIniciarWhatsapp();
                  }
                }}
                placeholder="Digite uma mensagem..."
                disabled={enviandoWhatsapp}
                style={{
                  flex: 1, padding: '0.6rem 1rem', borderRadius: '20px',
                  border: '1px solid #ddd', fontSize: '0.9rem', outline: 'none',
                }}
              />
              <button
                onClick={handleEnviarOuIniciarWhatsapp}
                disabled={!whatsappMsg.trim() || enviandoWhatsapp}
                style={{
                  width: '42px', height: '42px', borderRadius: '50%',
                  border: 'none', background: '#25D366', color: '#fff',
                  cursor: whatsappMsg.trim() && !enviandoWhatsapp ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: whatsappMsg.trim() && !enviandoWhatsapp ? 1 : 0.5,
                }}
              >
                <Send size={16} />
              </button>
            </div>
          ) : (
            <div style={{
              padding: '0.75rem', background: '#fff3cd', borderRadius: '8px',
              fontSize: '0.85rem', color: '#856404', display: 'flex', alignItems: 'center', gap: '0.5rem'
            }}>
              <AlertTriangle size={16} />
              Beneficiario sem telefone cadastrado. Atualize o telefone na aba "Visao Geral" para enviar mensagens.
            </div>
          )}
        </div>
      )}

      {/* DOCUMENTOS */}
      {activeTab === 'documentos' && (
        <div className="search-box">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '0.9rem', color: 'var(--gray-600)', margin: 0 }}>
              {documentos.length} documento(s) anexado(s)
            </h4>
            {!isAtendimentoFinalizado && (
              <button className="btn btn-primary" onClick={handleOpenUploadModal} style={{ padding: '0.4rem 0.75rem' }}>
                <Upload size={14} /> Anexar
              </button>
            )}
          </div>

          {documentos.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', textAlign: 'center', padding: '2rem' }}>
              Nenhum documento anexado
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
              {documentos.map((doc) => (
                <div key={doc.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.5rem 0.75rem', background: '#f8f9fa', borderRadius: '6px',
                  borderLeft: `3px solid ${doc.tipo_documento_cor || '#3498db'}`
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                    <FileText size={16} style={{ color: doc.tipo_documento_cor || '#3498db', flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.nome_original}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', display: 'flex', gap: '0.5rem' }}>
                        <span>{formatFileSize(doc.tamanho_bytes)}</span>
                        {doc.tem_texto_extraido && (
                          <span style={{ background: '#e8f5e9', color: '#27ae60', padding: '0 0.3rem', borderRadius: '3px' }}>IA</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button className="btn btn-secondary" style={{ padding: '0.3rem 0.5rem' }} onClick={() => handleDownload(doc)} title="Baixar">
                      <Download size={14} />
                    </button>
                    {!isAtendimentoFinalizado && (
                      <button className="btn btn-secondary" style={{ padding: '0.3rem 0.5rem', color: 'var(--danger)' }} onClick={() => handleDeleteDocumento(doc.id)} title="Excluir">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* HISTORICO */}
      {activeTab === 'historico' && (
        <div className="search-box">
          {atendimento.movimentacoes.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', textAlign: 'center', padding: '2rem' }}>
              Nenhuma movimentacao registrada
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {atendimento.movimentacoes.map((mov) => (
                <div key={mov.id} style={{
                  fontSize: '0.85rem', padding: '0.6rem 0.75rem',
                  background: '#f8f9fa', borderRadius: '6px',
                  borderLeft: '3px solid var(--primary)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{mov.tipo}</span>
                    <span style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>{formatDateTime(mov.criado_em)}</span>
                  </div>
                  {mov.coluna_origem && (
                    <div style={{ color: 'var(--gray-600)' }}>
                      {mov.coluna_origem} → {mov.coluna_destino}
                    </div>
                  )}
                  {mov.observacao && (
                    <div style={{ color: 'var(--gray-500)', marginTop: '0.15rem' }}>{mov.observacao}</div>
                  )}
                  <div style={{ color: 'var(--gray-400)', marginTop: '0.25rem' }}>por {mov.usuario}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ========== MODAIS ========== */}

      {/* Modal Edição de Contato */}
      {showContatoModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowContatoModal(false)}>
          <div className="modal-content" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h3>Editar Contato do Beneficiario</h3>
              <button className="modal-close" onClick={() => setShowContatoModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Telefone</label>
                <input
                  className="form-control"
                  value={contatoForm.telefone}
                  onChange={(e) => setContatoForm({ ...contatoForm, telefone: e.target.value })}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div className="form-group">
                <label>E-mail</label>
                <input
                  className="form-control"
                  type="email"
                  value={contatoForm.email}
                  onChange={(e) => setContatoForm({ ...contatoForm, email: e.target.value })}
                  placeholder="email@exemplo.com"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowContatoModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSalvarContato} disabled={salvandoContato}>
                {salvandoContato ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Upload */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h3>Anexar Documento</h3>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>&times;</button>
            </div>
            <div className="form-group">
              <label>Tipo de Documento</label>
              <select className="form-control" value={uploadTipoId} onChange={(e) => setUploadTipoId(e.target.value)}>
                <option value="">Selecione (opcional)...</option>
                {tiposDocumento.map((tipo) => (
                  <option key={tipo.id} value={tipo.id}>{tipo.nome}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Arquivo(s)</label>
              <div
                style={{
                  border: '2px dashed var(--gray-300)', borderRadius: '8px',
                  padding: '1.5rem', textAlign: 'center', cursor: 'pointer',
                  background: uploadFiles.length > 0 ? '#f0f9ff' : 'transparent'
                }}
                onClick={() => document.getElementById('file-upload-input').click()}
              >
                <input id="file-upload-input" type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
                {uploadFiles.length === 0 ? (
                  <>
                    <Upload size={28} style={{ color: 'var(--gray-400)', marginBottom: '0.5rem' }} />
                    <div style={{ color: 'var(--gray-500)', fontSize: '0.9rem' }}>Clique ou arraste arquivos</div>
                  </>
                ) : (
                  <div>
                    {uploadFiles.map((f, i) => (
                      <div key={i} style={{ fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                        {f.name} <span style={{ color: 'var(--gray-400)' }}>({formatFileSize(f.size)})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Descricao (opcional)</label>
              <input className="form-control" value={uploadDescricao} onChange={(e) => setUploadDescricao(e.target.value)} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleUpload} disabled={uploading || uploadFiles.length === 0}>
                {uploading ? 'Enviando...' : 'Enviar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Transferencia */}
      {showTransferModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h3>Transferir Atendimento</h3>
              <button className="modal-close" onClick={() => setShowTransferModal(false)}>&times;</button>
            </div>
            <div className="form-group">
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Setor de Destino</label>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.4rem' }}>
                {setores.filter((s) => s.id !== atendimento.setor.id).map((s) => {
                  const sel = transferData.setor_destino_id === String(s.id);
                  return (
                    <button key={s.id} type="button"
                      onClick={() => setTransferData({ ...transferData, setor_destino_id: String(s.id) })}
                      style={{
                        padding: '0.4rem 0.85rem', borderRadius: '8px', fontSize: '0.83rem',
                        fontWeight: sel ? 600 : 400,
                        border: sel ? '2px solid var(--primary,#2563eb)' : '2px solid #e2e8f0',
                        background: sel ? 'var(--primary,#2563eb)' : '#fff',
                        color: sel ? '#fff' : '#475569',
                        cursor: 'pointer', transition: 'all 0.15s',
                        boxShadow: sel ? '0 2px 8px rgba(37,99,235,0.15)' : 'none',
                      }}
                    >
                      {s.nome}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="form-group">
              <label>Motivo da Transferencia</label>
              <textarea className="form-control" rows="3" value={transferData.motivo} onChange={(e) => setTransferData({ ...transferData, motivo: e.target.value })} />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTransferModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleTransferir}>Transferir</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Finalizacao */}
      {showFinalizarModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h3>{ehTarefa ? 'Finalizar Tarefa' : 'Finalizar Atendimento'}</h3>
              <button className="modal-close" onClick={() => setShowFinalizarModal(false)}>&times;</button>
            </div>
            {ehTarefa && (atendimento.tipo_tarefa?.resultados || []).length > 0 && (
              <div className="form-group">
                <label>Resultado (volta para quem abriu a tarefa)</label>
                <select className="form-control" value={finalizarResultado} onChange={(e) => setFinalizarResultado(e.target.value)}>
                  <option value="">Selecione…</option>
                  {atendimento.tipo_tarefa.resultados.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}
            <div className="form-group">
              <label>{ehTarefa ? 'O que foi feito' : 'Motivo da Finalizacao'}</label>
              <textarea className="form-control" rows="3" value={finalizarMotivo} onChange={(e) => setFinalizarMotivo(e.target.value)} placeholder="Descreva como foi resolvido..." />
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowFinalizarModal(false)}>Cancelar</button>
              <button className="btn btn-success" onClick={handleFinalizar}>Finalizar</button>
            </div>
          </div>
        </div>
      )}

      <ParecerModal
        cardId={id}
        aberto={showParecer}
        onClose={() => setShowParecer(false)}
        onConcluido={loadData}
        onTransferir={(motivo) => { setTransferData({ setor_destino_id: '', motivo }); loadData(); setShowTransferModal(true); }}
      />

      {/* Modal Sigilo */}
      {showCadeadoModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h3>Gerenciar Sigilo</h3>
              <button className="modal-close" onClick={() => setShowCadeadoModal(false)}>&times;</button>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', cursor: 'pointer' }}>
                <input type="checkbox" checked={cadeadoAtivo} onChange={(e) => setCadeadoAtivo(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                <div>
                  <div style={{ fontWeight: 600 }}>Ativar Sigilo</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-600)' }}>Apenas usuarios autorizados poderao visualizar</div>
                </div>
              </label>
            </div>
            {cadeadoAtivo && (
              <div>
                <label style={{ fontWeight: 600, marginBottom: '0.5rem', display: 'block' }}>Usuarios Autorizados</label>
                <div style={{ maxHeight: '250px', overflowY: 'auto', border: '1px solid var(--gray-300)', borderRadius: '6px', padding: '0.5rem' }}>
                  {usuarios.filter((u) => u.id !== atendimento.atendente.id).map((usuario) => (
                    <label key={usuario.id} style={{
                      display: 'flex', alignItems: 'center', gap: '0.5rem',
                      padding: '0.5rem', cursor: 'pointer', borderRadius: '4px',
                      background: usuariosAutorizados.includes(usuario.id) ? 'var(--primary-light)' : 'transparent'
                    }}>
                      <input type="checkbox" checked={usuariosAutorizados.includes(usuario.id)} onChange={() => handleToggleUsuario(usuario.id)} />
                      <div>
                        <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{usuario.nome_completo}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{usuario.perfil}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="modal-footer" style={{ marginTop: '1rem' }}>
              <button className="btn btn-secondary" onClick={() => setShowCadeadoModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleSalvarCadeado}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* CSS para animação piscante */}
      <style>{`
        @keyframes pulse-alert {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>
  );
};

export default Atendimento;
